import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { CalendarView } from '@/types/calendar';
import { cn } from '@/utils/cn';

/**
 * CalendarNavigation Component
 *
 * Unified navigation header for all calendar views (Month, Week, Day).
 * Features:
 * - Navigation controls (prev/next)
 * - Dynamic title (formatted by parent)
 * - View switcher (Month/Week/Day)
 * - Today button
 * - Optional refresh button
 * - Responsive: Stacks on mobile
 */

interface CalendarNavigationProps {
  /**
   * Current view mode
   */
  view: CalendarView;

  /**
   * Callback when view changes
   */
  onViewChange: (view: CalendarView) => void;

  /**
   * Current date (used for navigation context, not displayed)
   */
  currentDate: Date;

  /**
   * Navigate to previous period
   */
  onPrev: () => void;

  /**
   * Navigate to next period
   */
  onNext: () => void;

  /**
   * Jump to today
   */
  onToday: () => void;

  /**
   * Formatted title to display (e.g., "December 2025", "Dec 15-21, 2025")
   */
  title: string;

  /**
   * Optional refresh callback
   */
  onRefresh?: () => void;

  /**
   * Loading state for refresh button
   */
  isLoading?: boolean;

  /**
   * Hide today button (e.g., when already on today)
   */
  hideTodayButton?: boolean;
}

export const CalendarNavigation = ({
  view,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  title,
  onRefresh,
  isLoading = false,
  hideTodayButton = false,
}: CalendarNavigationProps) => {
  const { t } = useTranslation('common');

  return (
    <div className="bg-white border-b border-gray-200 sticky top-[calc(4.1rem+44px)] z-20">
      {/* Desktop Layout: Single row with all controls */}
      <div className="hidden md:flex items-center justify-between px-4 sm:px-6 py-2.5">
        {/* Left: Month Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={onPrev}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('previous')}
          >
            <Icon name={ICONS.ANGLE_LEFT} size="sm" color="text-gray-600" ariaHidden />
          </button>

          <h2 className="text-xs sm:text-sm font-medium text-gray-900 min-w-0 sm:min-w-[160px] h-9 flex items-center justify-center text-center sm:whitespace-nowrap">
            {title}
          </h2>

          <button
            onClick={onNext}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('next')}
          >
            <Icon name={ICONS.ANGLE_RIGHT} size="sm" color="text-gray-600" ariaHidden />
          </button>
        </div>

        {/* Right: View Switcher + Actions */}
        <div className="flex items-center gap-3">
          {/* View Switcher */}
          <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => onViewChange('month')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium transition-colors',
                view === 'month'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              aria-label={t('month_view_label')}
              aria-pressed={view === 'month'}
            >
              {t('month_view')}
            </button>
            <button
              onClick={() => onViewChange('week')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium transition-colors border-x border-gray-300',
                view === 'week'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              aria-label={t('week_view_label')}
              aria-pressed={view === 'week'}
            >
              {t('week_view')}
            </button>
            <button
              onClick={() => onViewChange('day')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium transition-colors',
                view === 'day'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              aria-label={t('day_view_label')}
              aria-pressed={view === 'day'}
            >
              {t('day_view')}
            </button>
          </div>

          {/* Today Button - styled to match view switcher height */}
          {!hideTodayButton && (
            <button
              onClick={onToday}
              className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {t('today')}
            </button>
          )}

          {/* Refresh Button */}
          {onRefresh && (
            <Tooltip content={t('refresh_calendar', { defaultValue: 'Refresh calendar' })}>
              <button
                onClick={onRefresh}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t('refresh_calendar')}
                disabled={isLoading}
              >
                <Icon
                  name={ICONS.REFRESH}
                  size="sm"
                  color="text-gray-600"
                  className={isLoading ? 'animate-spin' : ''}
                  ariaHidden
                />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Mobile Layout: Stacked */}
      <div className="md:hidden">
        {/* Top Row: Navigation */}
        <div className="flex items-center justify-center gap-2 px-4 py-3 border-b border-gray-200">
            <button
              onClick={onPrev}
              className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('previous')}
            >
              <Icon name={ICONS.ANGLE_LEFT} size="sm" color="text-gray-600" ariaHidden />
            </button>

            <h2 className="text-sm font-medium text-gray-900 h-9 flex items-center justify-center whitespace-nowrap">
              {title}
            </h2>

            <button
              onClick={onNext}
              className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('next')}
            >
              <Icon name={ICONS.ANGLE_RIGHT} size="sm" color="text-gray-600" ariaHidden />
            </button>

            {!hideTodayButton && (
              <button
                onClick={onToday}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t('go_to_today')}
              >
                <Icon name={ICONS.CALENDAR} size="sm" color="text-gray-600" ariaHidden />
              </button>
            )}

            {onRefresh && (
              <Tooltip content={t('refresh_calendar', { defaultValue: 'Refresh calendar' })}>
                <button
                  onClick={onRefresh}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label={t('refresh_calendar')}
                  disabled={isLoading}
                >
                  <Icon
                    name={ICONS.REFRESH}
                    size="sm"
                    color="text-gray-600"
                    className={isLoading ? 'animate-spin' : ''}
                    ariaHidden
                  />
                </button>
              </Tooltip>
            )}
        </div>

        {/* Bottom Row: View Switcher */}
        <div className="flex items-center justify-center px-4 py-3">
          <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden w-full max-w-sm">
            <button
              onClick={() => onViewChange('month')}
              className={cn(
                'flex-1 px-3 py-2 text-sm font-medium transition-colors',
                view === 'month'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              aria-label={t('month_view_label')}
              aria-pressed={view === 'month'}
            >
              {t('month_view')}
            </button>
            <button
              onClick={() => onViewChange('week')}
              className={cn(
                'flex-1 px-3 py-2 text-sm font-medium transition-colors border-x border-gray-300',
                view === 'week'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              aria-label={t('week_view_label')}
              aria-pressed={view === 'week'}
            >
              {t('week_view')}
            </button>
            <button
              onClick={() => onViewChange('day')}
              className={cn(
                'flex-1 px-3 py-2 text-sm font-medium transition-colors',
                view === 'day'
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              aria-label={t('day_view_label')}
              aria-pressed={view === 'day'}
            >
              {t('day_view')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
